import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { CalendarDays, Repeat, Search, Trash2 } from "lucide-react";
import { adminEvents, imgUrl } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const EventEmbedView = ({ node, updateAttributes, deleteNode, selected }) => {
  const { data: events } = useQuery({ queryKey: ["admin-events"], queryFn: adminEvents, staleTime: 60_000 });
  const [q, setQ] = useState("");
  const [picking, setPicking] = useState(!node.attrs.id);
  const ev = useMemo(() => (events || []).find((e) => e.id === node.attrs.id), [events, node.attrs.id]);
  const options = useMemo(() => (events || []).filter((e) => e.published !== false && (!q || e.title.toLowerCase().includes(q.toLowerCase()))).sort((a, b) => Number(a.is_past) - Number(b.is_past) || (a.date || "").localeCompare(b.date || "")).slice(0, 8), [events, q]);
  const pick = (e) => { updateAttributes({ id: e.id, title: e.title }); setPicking(false); setQ(""); };
  return (
    <NodeViewWrapper as="div" data-type="event" className={cn("hk-event", selected && "is-selected")} contentEditable={false} data-testid="editor-event">
      {picking || !node.attrs.id ? (
        <div className="hk-event-picker" data-testid="editor-event-picker">
          <p className="hk-event-eyebrow"><CalendarDays className="h-3.5 w-3.5" /> Vel eit arrangement</p>
          <label className="hk-event-search"><Search className="h-4 w-4" /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i arrangementa …" data-testid="editor-event-search" /></label>
          <ul>
            {options.map((e) => <li key={e.id}><button type="button" onMouseDown={(ev2) => ev2.preventDefault()} onClick={() => pick(e)} data-testid={`editor-event-option-${e.id}`}><span className="tabular">{fmtDate(e.date, "d. MMM yyyy")}</span><span className="truncate">{e.title}</span>{e.is_past && <span className="hk-muted">passert</span>}</button></li>)}
            {!options.length && <li className="hk-muted px-3 py-2 text-[13px]">{events ? "Ingen treff" : "Hentar arrangement …"}</li>}
          </ul>
          <div className="mt-2 flex justify-end gap-1">
            {node.attrs.id && <button type="button" onClick={() => setPicking(false)} className="hk-link-btn">Avbryt</button>}
            <button type="button" onClick={deleteNode} className="hk-link-btn is-danger" data-testid="editor-event-remove">Fjern blokka</button>
          </div>
        </div>
      ) : (
        <div className="hk-event-card" data-drag-handle>
          <div className="hk-event-thumb">{(ev?.image || ev?.images?.[0]) ? <img src={imgUrl(ev.image || ev.images[0], 480)} alt="" /> : <CalendarDays className="h-5 w-5" />}</div>
          <div className="min-w-0 flex-1">
            <p className="hk-event-eyebrow">Arrangement{ev?.date && <> · {fmtDate(ev.date, "EEEE d. MMMM")}{ev.start_time ? ` kl. ${ev.start_time}` : ""}</>}</p>
            <p className="hk-event-title">{ev?.title || node.attrs.title || "Arrangement"}</p>
            {ev === undefined && events && <p className="hk-muted text-[12px]">Finst ikkje lenger – vel eit anna</p>}
          </div>
          <div className="hk-block-tools is-static">
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setPicking(true)} title="Byt arrangement" aria-label="Byt arrangement" data-testid="editor-event-change"><Repeat className="h-3.5 w-3.5" /></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={deleteNode} className="is-danger" title="Fjern" aria-label="Fjern" data-testid="editor-event-remove"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
};

/** <div data-type="event" data-id><a href="/arrangement/id">Title</a></div> – hydrated to a live EventCard on the public page. */
export const EventEmbed = Node.create({
  name: "eventEmbed",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      id: { default: null, parseHTML: (el) => el.getAttribute("data-id") },
      title: { default: "", parseHTML: (el) => el.querySelector("a")?.textContent?.trim() || "" },
    };
  },
  parseHTML() { return [{ tag: 'div[data-type="event"]' }]; },
  renderHTML({ node }) { return ["div", { "data-type": "event", "data-id": node.attrs.id || "" }, ["a", { href: `/arrangement/${node.attrs.id || ""}` }, node.attrs.title || "Sjå arrangementet"]]; },
  addNodeView() { return ReactNodeViewRenderer(EventEmbedView); },
});
